import {
  type MetaFunction,
  type ActionFunctionArgs,
  data,
  type LoaderFunctionArgs,
  redirect,
  Form,
  Link,
  useLoaderData,
} from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { deleteAll } from '#app/controls/lesson-plan-modification.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { executeQuery } from '#app/utils/cms.server.ts'
import { requireControlsToBeEnabled } from '#app/utils/controls.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import {
  type LessonData,
  getUserLessonPlanModifications,
} from '#app/utils/lesson-plan-modification.server.ts'
import { getModificationDayNames } from '#app/utils/lesson-plan-modification.ts'
import { Status } from '#app/utils/status.ts'

export const trainingProgramName = 'Ages 3-5'

interface ProgramData {
  _id: string
  slug: {
    current: string
  }
  lessons: LessonData[]
}

const getProgramData = async (): Promise<ProgramData[]> => {
  const filter = `_type == 'trainingProgram' && title.en == "${trainingProgramName}"`

  const lessonAttributes = `_id, number, days[]->{_id, name}`
  const projection = `_id, slug, lessons[]->{${lessonAttributes}}`

  const query = `*[${filter}] | order(_id asc) {${projection}}[]`
  const result = await executeQuery(query)

  if (result.status !== Status.Success) {
    throw data({}, { status: 500 })
  }

  const programData = result.data as ProgramData[]
  return programData
}

export async function loader({ request }: LoaderFunctionArgs) {
  requireControlsToBeEnabled()

  const currentUserId = await getUserId(request)

  const lessonPlanModifications = await getUserLessonPlanModifications({
    includeUser: true,
  })

  const trainingPrograms = await getProgramData()

  const trainingProgramsById: Record<string, ProgramData> = {}
  trainingPrograms.forEach(
    trainingProgram =>
      (trainingProgramsById[trainingProgram._id] = trainingProgram),
  )

  return {
    lessonPlanModifications,
    trainingPrograms: trainingProgramsById,
    currentUserId,
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const intent = formData.get('intent')

  const redirectPath = '/controls/user-lesson-plans'

  switch (intent) {
    case 'deleteAll': {
      await deleteAll()
      return redirect(redirectPath)
    }
  }
}

export default function LessonPlanModificationsControlRoute() {
  const data = useLoaderData<typeof loader>()
  const { lessonPlanModifications, trainingPrograms, currentUserId } = data

  return (
    <>
      <ul className="flex gap-3">
        <li>
          <Link to={'/controls'}>Controls</Link>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">
            User Lesson Plans
          </span>
        </li>
      </ul>
      <div className="my-6">
        <Form method="POST">
          {currentUserId && (
            <Link to={`/controls/user-lesson-plans/${currentUserId}`}>
              Current user
            </Link>
          )}
          <Button variant="link" type="submit" name="intent" value="deleteAll">
            Delete all
          </Button>
        </Form>
      </div>
      <table className="my-6 w-full table-auto border-collapse border">
        <thead>
          <tr>
            <th className="border p-2">ID</th>
            <th className="border p-2">User ID</th>
            <th className="border p-2">Email</th>
            <th className="border p-2">Training Program</th>
            <th className="border p-2">Lesson Number</th>
            <th className="border p-2">Days</th>
          </tr>
        </thead>
        <tbody>
          {lessonPlanModifications.map(modification => {
            const { trainingProgramId, lessonId } = modification

            const trainingProgram = trainingPrograms[trainingProgramId]
            if (!trainingProgram) return null

            const trainingProgramName = trainingProgram.slug.current
            const lesson = trainingProgram.lessons.find(
              lesson => lesson._id === lessonId,
            )

            return (
              <tr key={modification.id}>
                <td className="border p-2">{modification.id}</td>
                <td className="border p-2">{modification?.user?.id}</td>
                <td className="border p-2">{modification?.user?.email}</td>
                <td className="border p-2">{trainingProgramName}</td>
                <td className="border p-2">{lesson?.number}</td>
                <td className="border p-2">
                  {getModificationDayNames(modification?.days, lesson)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

export const meta: MetaFunction = () => {
  return [{ title: `Controls > User Lesson Plans | ${siteTitle}` }]
}
